import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { router } from './router';
import { TEST_DEPLOY_BYPASS } from './lib/featureFlags';
import './styles/tailwind.css';
import './styles/fonts.css';

/**
 * MSW в dev — браузерный worker перехватывает /api/* запросы и отвечает
 * фикстурами из @pomoshnik/shared. Пока нет реального Edge.
 *
 * `import.meta.env.DEV` — Vite вырезает эту ветку при build для production.
 *
 * **TEMPORARY (Phase F5, 2026-05-21):** `TEST_DEPLOY_BYPASS` (env
 * `VITE_TEST_DEPLOY=true`) тоже включает MSW в production build для
 * Cloudflare Pages test-деплоя — реального backend ещё нет, без MSW
 * `/api/*` отдаёт SPA fallback (HTML) и JSON.parse падает. Парный
 * kill-switch для Workbox SW — `vite.config.ts selfDestroying`. Удалить
 * после Phase 9 (real auth + real Edge). См. `featureFlags.ts`.
 */
async function enableMocking() {
  if (!import.meta.env.DEV && !TEST_DEPLOY_BYPASS) return;
  // B-093 (fix 2026-06-19): снимаем любой ранее зарегистрированный SW перед
  // стартом MSW. Раньше эту чистку делал self-destroying PWA dev-SW, но он сам
  // сидел на scope `/` и при activate unregister'ил MSW (last-wins) → не
  // оставалось ни одного SW → /api/* уходил в SPA-fallback и authed-экраны
  // кабинета падали. Теперь PWA dev-SW в DEV выключен (vite.config.ts
  // devOptions.enabled: false), а stale Workbox от прошлого `pnpm preview`
  // снимаем здесь вручную — единственным SW остаётся MSW (mockServiceWorker.js).
  if ('serviceWorker' in navigator) {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  }
  const { worker } = await import('./mocks/browser');
  await worker.start({
    onUnhandledRequest: 'bypass',
    serviceWorker: { url: '/mockServiceWorker.js' },
  });
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

void enableMocking().then(() => {
  createRoot(rootEl).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );
});
